import { type ReactNode, useState } from 'react'

export type Step = {
  title: string
  /** One sentence. Two at the absolute outside — this is a page to scan. */
  line: string
  shot: ReactNode
}

/**
 * A section's points, as a rail you step down.
 *
 * Three points, one open at a time: the open one shows its sentence and its
 * shot, the others are titles only. That is what lets a section carry three
 * claims without becoming three walls of text — the reader sees all three
 * headings at once and chooses which to open, so the page stays scannable at
 * full length rather than only at the top.
 *
 * The marker is a square on a hairline rail, filled for the open point and faint
 * for the rest. Square because nothing in this system is rounded, and ink
 * because ink is how it says "active" — a colored dot would be the one thing
 * the palette rules keep out.
 *
 * Clicking rather than hovering. A hover-driven rail changes under the cursor on
 * the way to something else, and on a touch screen it has no behavior at all.
 */
export function Steps({ items }: { items: Step[] }) {
  const [open, setOpen] = useState(0)

  return (
    <div className="mk-steps">
      <ol className="mk-steps__rail">
        {items.map((item, i) => {
          const isOpen = i === open
          return (
            <li
              className="mk-step"
              data-open={isOpen ? '' : undefined}
              key={item.title}
            >
              <button
                type="button"
                className="mk-step__head"
                aria-expanded={isOpen}
                onClick={() => setOpen(i)}
              >
                <span className="mk-step__mark" aria-hidden />
                <span className="mk-step__title">{item.title}</span>
              </button>
              {/* Rendered only when open: a collapsed point is a heading, and
                  keeping its sentence in the document as hidden text would put
                  three paragraphs back on the page in every screen reader. */}
              {isOpen ? <p className="mk-step__line">{item.line}</p> : null}
            </li>
          )
        })}
      </ol>

      <div className="mk-steps__show">{items[open].shot}</div>
    </div>
  )
}
